import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { fmtDate, fmtDateTime } from '../lib/datetime.js';
import MonthPicker, { prevMonthOfNow } from '../components/MonthPicker.jsx';

const FLAG_LABEL = {
  no_show: 'No-show',
  substitute: 'Substitute',
  under_use: 'Under-use',
  unbooked: 'Unbooked',
  auto_closed: 'Auto-closed',
};
const FLAG_COLOR = {
  no_show: 'bg-red-100 text-red-800',
  substitute: 'bg-amber-100 text-amber-800',
  under_use: 'bg-amber-50 text-amber-700',
  unbooked: 'bg-slate-100 text-slate-700',
  auto_closed: 'bg-yellow-100 text-yellow-800',
};

export default function CrossCheck() {
  // Default to the previous month — monthly cross-checks land after the
  // CrewLink data for the prior month is finalised.
  const [month, setMonth] = useState(prevMonthOfNow());
  const [runs, setRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [flags, setFlags] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadRuns = async () => {
    try {
      const r = await api.crossCheck.runs({ month });
      setRuns(r.runs);
      const latest = r.runs.find((x) => x.is_latest) || r.runs[0] || null;
      setActiveRun(latest);
    } catch (e) {
      setErr(e.message);
    }
  };
  const loadFlags = async (runId) => {
    if (!runId) {
      setFlags([]);
      return;
    }
    try {
      const r = await api.crossCheck.flags(runId, typeFilter ? { type: typeFilter } : {});
      setFlags(r.flags);
    } catch (e) {
      setErr(e.message);
    }
  };
  const loadPilots = async () => {
    try {
      const r = await api.crossCheck.statsPilots(month);
      setPilots(r.pilots);
    } catch (e) {
      // 400 if no run yet — fine, silent.
      setPilots([]);
    }
  };

  useEffect(() => {
    loadRuns();
    loadPilots();
  }, [month]); // eslint-disable-line
  useEffect(() => {
    loadFlags(activeRun?.id);
  }, [activeRun, typeFilter]); // eslint-disable-line

  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.crossCheck.run({ month, includeSimulated: true });
      await Promise.all([loadRuns(), loadPilots()]);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const updateFlag = async (id, patch) => {
    try {
      await api.crossCheck.patchFlag(id, patch);
      await loadFlags(activeRun?.id);
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-cathay-700">Cross-check</h1>
          <p className="text-sm text-slate-600 mt-1">
            Compares the monthly booking sheet against actual sign-in events. Re-runs are
            idempotent — the latest run's flags supersede previous ones; history is retained.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <MonthPicker value={month} onChange={setMonth} />
          <button
            onClick={run}
            disabled={busy}
            className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-2 rounded text-sm"
          >
            Run cross-check
          </button>
        </div>
      </header>

      {err && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}

      {/* Run summary */}
      {activeRun?.flags_summary && (
        <section className="bg-white rounded shadow-sm border p-4">
          <div className="text-sm text-slate-600">
            Latest run — {fmtDateTime(activeRun.ran_at)} by{' '}
            {activeRun.ran_by_name || 'admin'}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mt-3 text-sm">
            <Stat label="No-shows" value={activeRun.flags_summary.no_show} highlight="red" />
            <Stat label="Substitutes" value={activeRun.flags_summary.substitute} highlight="amber" />
            <Stat label="Under-use" value={activeRun.flags_summary.under_use} highlight="amber" />
            <Stat label="Unbooked" value={activeRun.flags_summary.unbooked} highlight="slate" />
            <Stat
              label="Auto-closed"
              value={activeRun.flags_summary.auto_closed || 0}
              highlight="yellow"
            />
            <Stat label="Total" value={activeRun.flags_summary.total} />
          </div>
        </section>
      )}

      {/* Worst offenders */}
      <section className="bg-white rounded shadow-sm border p-4">
        <h2 className="font-medium text-slate-800">Worst offenders</h2>
        <table className="min-w-full mt-2 text-sm">
          <thead>
            <tr className="text-left text-slate-500 text-xs">
              <th className="py-1">CathayID</th>
              <th className="py-1 text-right">No-shows</th>
              <th className="py-1 text-right">Under-use sessions</th>
              <th className="py-1 text-right">Under-use min</th>
              <th className="py-1 text-right">Substitutes</th>
              <th className="py-1 text-right">Unbooked uses</th>
              <th className="py-1 text-right">Auto-closed</th>
            </tr>
          </thead>
          <tbody>
            {pilots.map((p, i) => (
              <tr key={p.cathay_id || i} className="border-t border-slate-100">
                <td className="py-1.5 font-mono uppercase">{p.cathay_id}</td>
                <td className="py-1.5 text-right text-red-700 font-medium">{p.no_shows}</td>
                <td className="py-1.5 text-right">{p.under_use_count}</td>
                <td className="py-1.5 text-right">{p.under_use_minutes || 0}</td>
                <td className="py-1.5 text-right">{p.substitute_count}</td>
                <td className="py-1.5 text-right">{p.unbooked_count}</td>
                <td className="py-1.5 text-right text-yellow-700 font-medium">{p.auto_closed_count || 0}</td>
              </tr>
            ))}
            {pilots.length === 0 && (
              <tr>
                <td colSpan={7} className="py-4 text-center text-slate-500">
                  No worst-offender data — run a cross-check first.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {/* Flag queue */}
      <section className="bg-white rounded shadow-sm border p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-slate-800">Flags ({flags.length})</h2>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1 text-sm"
          >
            <option value="">All types</option>
            <option value="no_show">No-shows</option>
            <option value="substitute">Substitutes</option>
            <option value="under_use">Under-use</option>
            <option value="unbooked">Unbooked</option>
            <option value="auto_closed">Auto-closed</option>
          </select>
        </div>
        <table className="min-w-full mt-2 text-sm">
          <thead>
            <tr className="text-left text-slate-500 text-xs">
              <th className="py-1">Type</th>
              <th className="py-1">Date / hour</th>
              <th className="py-1">Booked</th>
              <th className="py-1">Actual</th>
              <th className="py-1">Notes</th>
              <th className="py-1">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {flags.map((f) => (
              <tr key={f.id} className="border-t border-slate-100 align-top">
                <td className="py-1.5">
                  <span className={'px-2 py-0.5 rounded text-xs ' + FLAG_COLOR[f.type]}>
                    {FLAG_LABEL[f.type]}
                  </span>
                </td>
                <td className="py-1.5 text-slate-700">
                  {f.booking_date
                    ? `${fmtDate(f.booking_date)} ${String(f.start_hour).padStart(2, '0')}:00–${String(f.end_hour).padStart(2, '0')}:00`
                    : f.signin_rounded_hour
                      ? fmtDateTime(f.signin_rounded_hour)
                      : '—'}
                  <div className="text-xs text-slate-500">
                    {f.booking_device_name || f.session_device_name || ''}
                  </div>
                </td>
                <td className="py-1.5 font-mono">{f.booking_cathay_id || '—'}</td>
                <td className="py-1.5 font-mono">{f.session_cathay_id || '—'}</td>
                <td className="py-1.5 text-slate-600">{f.notes || ''}</td>
                <td className="py-1.5">
                  <span
                    className={
                      'text-xs px-2 py-0.5 rounded ' +
                      (f.status === 'open'
                        ? 'bg-slate-100 text-slate-700'
                        : f.status === 'resolved'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-slate-200 text-slate-500')
                    }
                  >
                    {f.status}
                  </span>
                </td>
                <td className="py-1.5 text-right space-x-1">
                  {f.status === 'open' && (
                    <>
                      <button
                        className="text-xs text-green-700 hover:underline"
                        onClick={() => updateFlag(f.id, { status: 'resolved' })}
                      >
                        Resolve
                      </button>
                      <button
                        className="text-xs text-slate-600 hover:underline"
                        onClick={() => updateFlag(f.id, { status: 'dismissed' })}
                      >
                        Dismiss
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {flags.length === 0 && (
              <tr>
                <td colSpan={7} className="py-6 text-center text-slate-500">
                  No flags. Run cross-check to populate.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  const tones = {
    red: 'text-red-700 bg-red-50',
    amber: 'text-amber-700 bg-amber-50',
    yellow: 'text-yellow-700 bg-yellow-50',
    slate: 'text-slate-700 bg-slate-100',
  };
  return (
    <div className={'rounded p-2 ' + (highlight ? tones[highlight] : 'bg-slate-50')}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-medium">{value}</div>
    </div>
  );
}
